import { createTheme } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { APIProvider } from '@vis.gl/react-google-maps';
import ConfigProvider from 'antd/es/config-provider';
import en_US from 'antd/es/locale/en_US';
import vi_VN from 'antd/es/locale/vi_VN';
import theme from 'antd/es/theme';
import ScrollRestoration from 'components/ScrollRestoration';
import SplashScreen from 'components/SplashScreen';
import DOMPurify from 'dompurify';
import { appPrimaryColorSelector } from 'features/appUI/selectors';
import { LanguageProvider, useLanguage } from 'lib/i18n/LanguageProvider';
import { useAppSelector } from 'lib/stores/hook';
import { Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import AppRouterConfig from 'routes/AppRouterConfig';
import { themeOptions } from 'styles/MuiTheme';
import { setAppFavicon } from 'utils/getAppLogo';
import dayjs from 'utils/moment/dayjsConfig';

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const AppContent = () => {
  const appPrimaryColor = useAppSelector(appPrimaryColorSelector);
  const { language } = useLanguage();

  useEffect(() => {
    dayjs.locale(language);
  }, [language]);

  useEffect(() => {
    setAppFavicon();
  }, []);

  return (
    <APIProvider apiKey={import.meta.env.VITE_APP_GM_API_KEY}>
      <ThemeProvider theme={createTheme(themeOptions)}>
        <StyledEngineProvider injectFirst>
          <ConfigProvider
            locale={language === 'en' ? en_US : vi_VN}
            theme={{
              algorithm: theme.defaultAlgorithm,
              token: {
                colorPrimary: appPrimaryColor,
                borderRadius: 5,
              },
            }}
          >
            <Suspense
              fallback={
                <ScrollRestoration>
                  <SplashScreen fullScreen />
                </ScrollRestoration>
              }
            >
              <RouterProvider router={AppRouterConfig} />
            </Suspense>
          </ConfigProvider>
        </StyledEngineProvider>
      </ThemeProvider>
    </APIProvider>
  );
};

const App = () => (
  <LanguageProvider>
    <AppContent />
  </LanguageProvider>
);

export default App;
